import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { Star, ShoppingBag, ArrowRight, X as XIcon } from "lucide-react";
import { MaskLines, Reveal, Eyebrow } from "@/components/Motion";
import { CTAPair, CallButton } from "@/components/CTAButtons";
import { Marquee } from "@/components/Marquee";
import { SITE, HOURS } from "@/data/site";
import { useOpenStatus } from "@/hooks/useOpenStatus";

const RatingSeal = () => (
  <div className="relative h-32 w-32 sm:h-36 sm:w-36" data-testid="rating-badge">
    <svg viewBox="0 0 200 200" className="spin-slow absolute inset-0 h-full w-full">
      <defs><path id="circ" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" /></defs>
      <circle cx="100" cy="100" r="98" fill="#F29A2E" />
      <text fontFamily="DM Mono, monospace" fontSize="14" fill="#1B110D">
        <textPath href="#circ" textLength="486" lengthAdjust="spacing">★ 108 GOOGLE REVIEWS ★ 4.8 STARS ON GOOGLE </textPath>
      </text>
    </svg>
    <div className="absolute inset-0 grid place-items-center text-center text-cocoa">
      <div>
        <p className="font-display text-4xl font-semibold leading-none">{SITE.rating}</p>
        <div className="mt-1 flex justify-center gap-0.5">
          {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3 w-3 fill-hibiscus text-hibiscus" />)}
        </div>
      </div>
    </div>
  </div>
);

const HeroImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={onLeave}>
      <motion.div
        style={{ rotateX: rx, rotateY: ry }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 28px 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 28px 28px)" }}
        transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
        className="arch relative mx-auto aspect-[4/5] w-full max-w-[520px] overflow-hidden bg-hibiscus"
      >
        <motion.img style={{ y, scale: 1.2 }} src="/images/hero-takeout.jpg" alt="Baked chicken with rice and beans in a takeout tray" className="h-full w-full object-cover" data-testid="hero-image" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,transparent_35%,rgba(27,17,13,0.55))]" />
      </motion.div>
      <motion.div initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 140, damping: 14, delay: 1.2 }}
        className="absolute -left-2 bottom-10 sm:-left-8">
        <RatingSeal />
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }}
        className="absolute -right-1 top-16 rounded-2xl border border-cream/15 bg-cocoa/70 px-4 py-3 text-cream backdrop-blur-xl sm:-right-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mango">Para llevar</p>
        <p className="mt-1 flex items-center gap-2 text-sm font-semibold"><ShoppingBag className="h-4 w-4" /> Takeout, ready fast</p>
      </motion.div>
    </div>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden bg-cocoa pb-20 pt-36 text-cream sm:pt-40 lg:pb-28" data-testid="home-hero">
    <div className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-hibiscus/25 blur-[120px]" />
    <div className="pointer-events-none absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full bg-palm/40 blur-[120px]" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
          <Eyebrow className="text-mango">Cocina Dominicana & Puertorriqueña · Grand Rapids, MI</Eyebrow>
        </motion.div>
        <MaskLines
          testId="hero-headline"
          delay={0.15}
          className="mt-6 font-display text-[2.6rem] font-medium leading-[0.98] tracking-tight sm:text-6xl lg:text-[3.9rem] xl:text-[4.3rem]"
          lines={[
            <>Authentic <em className="text-mango">Dominican</em></>,
            <>& <em className="text-hibiscus">Puerto Rican</em> flavors</>,
            <>in the heart of</>,
            <>Grand Rapids.</>,
          ]}
        />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9 }}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-cream/80 sm:text-lg">
            Pollo guisado, baked chicken, yellow rice and fresh empanadas — cooked the way our family does it. Call ahead for takeout, or pull up a chair and be treated like family.
          </p>
          <CTAPair prefix="hero" className="mt-10" />
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 font-mono text-[11px] uppercase tracking-[0.18em] text-cream/70">
            <span>Dine-in</span><span className="h-1 w-1 rounded-full bg-mango" />
            <span>Takeout</span><span className="h-1 w-1 rounded-full bg-mango" />
            <span>Lunch spot</span><span className="h-1 w-1 rounded-full bg-mango" />
            <span>$</span>
          </div>
        </motion.div>
      </div>
      <div className="lg:col-span-5"><HeroImage /></div>
    </div>
  </section>
);

const HoursStrip = () => {
  const { weekday, isOpen, label } = useOpenStatus();
  return (
    <section className="border-b border-cocoa/10 bg-cream py-14" data-testid="home-hours-strip">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <Eyebrow className="text-hibiscus">Horario</Eyebrow>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl">Open Mon–Sat, <em>10 to 6.</em></h2>
          </div>
          <span className={`inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] ${isOpen ? "bg-palm text-cream" : "bg-cocoa text-cream"}`} data-testid="hours-strip-status">
            <span className={`h-2 w-2 rounded-full ${isOpen ? "bg-[#7EE2A8]" : "bg-mango"}`} /> {label}
          </span>
        </div>
        <div className="mt-8 grid grid-cols-7 gap-1.5 sm:gap-3">
          {HOURS.map((h, i) => (
            <Reveal key={h.short} delay={i * 0.05}>
              <div className={`flex h-full flex-col items-center rounded-2xl border px-1 py-4 text-center sm:py-6 ${h.short === weekday ? "border-hibiscus bg-hibiscus text-cream" : h.open ? "border-cocoa/15 bg-white/60" : "border-dashed border-cocoa/25 text-cocoa/60"}`} data-testid={`hours-day-${h.short.toLowerCase()}`}>
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] sm:text-xs">{h.short}</span>
                {h.open ? (
                  <span className="mt-2 font-display text-sm sm:text-xl">10–6</span>
                ) : (
                  <span className="mt-2 flex items-center gap-1 font-display text-sm italic sm:text-xl"><XIcon className="hidden h-4 w-4 sm:block" />Closed</span>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const STEPS = [
  { n: "01", es: "Llama", t: `Call ${SITE.phone}`, d: "Tell us what you're craving — we'll get it going." },
  { n: "02", es: "Escoge", t: "Pick a meat + any side", d: "Baked chicken, stew chicken, steak — with rice and beans, gandules or yellow rice." },
  { n: "03", es: "Recoge", t: "Pick it up hot", d: `Swing by ${SITE.street} and take a little Caribbean home.` },
];

const Takeout = () => (
  <section className="bg-cream py-24 sm:py-32" data-testid="home-takeout">
    <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-20">
      <Reveal className="lg:col-span-5">
        <div className="relative overflow-hidden rounded-[28px] [clip-path:polygon(0_0,100%_6%,100%_100%,0_94%)]">
          <img src="/images/plate-chicken.jpg" alt="Baked chicken plate with rice and beans" className="aspect-[4/5] w-full object-cover transition-transform duration-[1.6s] hover:scale-105" />
        </div>
      </Reveal>
      <div className="lg:col-span-7">
        <Reveal>
          <Eyebrow className="text-hibiscus">Para llevar · Takeout</Eyebrow>
          <h2 className="mt-4 font-display text-4xl leading-[1.02] sm:text-6xl">Lunch, the <em className="text-hibiscus">Caribbean</em> way — ready when you are.</h2>
        </Reveal>
        <div className="mt-12 divide-y divide-cocoa/15 border-y border-cocoa/15">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="group grid grid-cols-[auto_1fr] gap-6 py-7 sm:grid-cols-[80px_140px_1fr]" data-testid={`takeout-step-${s.n}`}>
                <span className="font-mono text-sm text-hibiscus">{s.n}</span>
                <span className="hidden font-display text-2xl italic text-cocoa/60 sm:block">{s.es}</span>
                <div className="col-span-2 sm:col-span-1">
                  <p className="text-xl font-semibold transition-transform duration-300 group-hover:translate-x-1">{s.t}</p>
                  <p className="mt-1 text-cocoa/75">{s.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal><CTAPair prefix="takeout" dark className="mt-10" /></Reveal>
      </div>
    </div>
  </section>
);

const Tile = ({ img, es, title, text, className = "", testId }) => (
  <motion.div
    initial={{ opacity: 0.15, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.12 }}
    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative overflow-hidden rounded-[28px] bg-cocoa ${className}`}
    data-testid={testId}
  >
    <img src={img} alt={title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110" />
    <div className="absolute inset-0 bg-gradient-to-t from-cocoa via-cocoa/30 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-6 text-cream sm:p-8">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mango">{es}</p>
      <h3 className="mt-2 font-display text-2xl sm:text-3xl">{title}</h3>
      {text && <p className="mt-2 max-w-md text-sm text-cream/85">{text}</p>}
    </div>
  </motion.div>
);

const KnownFor = () => (
  <section className="bg-cream pb-24 sm:pb-32" data-testid="home-known-for">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
        <div>
          <Eyebrow className="text-hibiscus">Lo más pedido</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-display text-4xl leading-[1.02] sm:text-6xl">What Grand Rapids <em>keeps coming back for.</em></h2>
        </div>
        <Link to="/menu" data-testid="known-for-menu-link" className="group inline-flex items-center gap-2 font-semibold text-hibiscus">
          See the full menu <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <div className="grid auto-rows-[300px] grid-cols-1 gap-4 sm:grid-cols-2 lg:auto-rows-[290px] lg:grid-cols-4">
        <Tile testId="bento-pollo-guisado" className="sm:col-span-2 lg:row-span-2" img="/images/bowl.jpg" es="Pollo guisado · Chicken & rice" title="What we're known for" text="Stewed chicken with yellow rice, and the chicken & rice plates our regulars order by name." />
        <Tile testId="bento-empanadas" className="sm:col-span-2" img="/images/empanadas.jpg" es="Empanadas" title="Beef Pie & Chicken Pie" text="Golden, flaky and the first thing reviewers mention." />
        <Tile testId="bento-tostones" img="/images/tostones.jpg" es="Tostones" title="Fried plantains" />
        <Tile testId="bento-parcha" img="/images/parcha.jpg" es="Jugo de parcha" title="Passion fruit juice" />
      </div>
    </div>
  </section>
);

const Family = () => (
  <section className="relative overflow-hidden bg-palm py-24 text-cream sm:py-32" data-testid="home-family">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <Reveal>
          <Eyebrow className="text-mango">Como en casa</Eyebrow>
          <p className="mt-6 font-display text-4xl leading-[1.05] sm:text-6xl lg:text-7xl">
            Walk in hungry.<br /><em className="text-mango">Leave treated like family.</em>
          </p>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-cream/85">
            Sabor Latino is a family-run Dominican and Puerto Rican kitchen on Cesar E. Chavez Ave. Reviewers rave about the empanadas, the pollo horneado, stewed chicken with yellow rice, steak with rice and beans — and that passion fruit juice.
          </p>
        </Reveal>
        <Reveal delay={0.1} className="mt-12 flex items-center gap-6">
          <p className="font-display text-7xl sm:text-8xl" data-testid="family-rating">{SITE.rating}</p>
          <div>
            <div className="flex gap-1">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-5 w-5 fill-mango text-mango" />)}</div>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-cream/80">Across {SITE.reviews} Google reviews</p>
          </div>
        </Reveal>
      </div>
      <Reveal className="lg:col-span-5" delay={0.15}>
        <div className="arch relative mx-auto aspect-[3/4] max-w-[440px] overflow-hidden border-[6px] border-mango/80">
          <img src="/images/family.jpg" alt="Family cooking together in a small kitchen" className="h-full w-full object-cover" />
        </div>
      </Reveal>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="bg-hibiscus py-24 text-cream" data-testid="home-final-cta">
    <Reveal className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 px-5 sm:px-8 lg:flex-row lg:items-end">
      <h2 className="max-w-3xl font-display text-4xl leading-[1.02] sm:text-6xl">¿Tienes hambre? <em className="text-cocoa">The kitchen's open till six.</em></h2>
      <div className="flex flex-wrap gap-3">
        <CallButton id="final-call-btn" className="!bg-cocoa hover:!bg-black" />
        <Link to="/visit" data-testid="final-visit-link" className="inline-flex items-center gap-2 rounded-full border border-cream/40 px-6 py-3.5 font-semibold transition-colors hover:bg-cream hover:text-hibiscus">Plan your visit <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <HoursStrip />
      <Takeout />
      <KnownFor />
      <Marquee />
      <Family />
      <FinalCTA />
    </>
  );
}
